import { ANNOTATION_GRAPH_TYPES } from "constants/annotations";
import { AnnotationSubtype } from "graphql_globals";
import {
  updateDocumentNode,
  updateDesignationOnDocument,
  updateAnnotationOnDocument,
} from "common/meeting/util";

import { SOCKET_EVENT_PAGE_TYPES } from "./meeting_events";

function annotationGraphTypeFromSocket({ attributes }) {
  switch (attributes.type) {
    case "whitebox":
      return {
        __typename: ANNOTATION_GRAPH_TYPES.WHITEBOX,
        subtype: AnnotationSubtype.WHITEBOX,
      };
    case "checkmark":
      return {
        __typename: ANNOTATION_GRAPH_TYPES.CHECKMARK,
        subtype:
          attributes.subtype === "radio_checkmark"
            ? AnnotationSubtype.RADIO_CHECKMARK
            : AnnotationSubtype.CHECKMARK,
      };
    case "text":
      return {
        __typename: ANNOTATION_GRAPH_TYPES.TEXT,
        subtype: attributes.subtype.toUpperCase(), // Graph is capitalizing enums on transmit
        text: attributes.data.text || "",
        static: false,
      };
    case "image":
      return {
        __typename: ANNOTATION_GRAPH_TYPES.IMAGE,
        subtype: AnnotationSubtype.SEAL, // This is the only image subtype
        notarialActEnum: attributes.data.notarial_act.toUpperCase(),
        notarialActPrincipals: [],
        asset: {
          __typename: "SecureUrl",
          url: attributes.data.url,
        },
      };
    case "vector_graphic": {
      const subtype =
        attributes.subtype === "signature"
          ? AnnotationSubtype.SIGNATURE
          : AnnotationSubtype.INITIALS;
      return {
        __typename: ANNOTATION_GRAPH_TYPES.VECTOR_GRAPHIC,
        kind: subtype,
        subtype,
        pngAsset: {
          __typename: "SecureUrl",
          url: attributes.data.png_url,
        },
        asset: {
          __typename: "SecureUrl",
          url: attributes.data.url,
        },
      };
    }
    default:
      throw new Error(`Unknown socket event annotation type ${attributes.type}.`);
  }
}

function createNewAnnotationFromSocketData(socketData) {
  const {
    attributes,
    relationships: { author, annotation_designation, meeting },
  } = socketData;
  return {
    id: attributes.gid,
    authorId: author.data.gid,
    annotationDesignationId: annotation_designation ? annotation_designation.data.gid : null,
    location: {
      __typename: "AnnotationLocation",
      coordinateSystem: attributes.coordinate_system,
      page: attributes.page,
      pageType: SOCKET_EVENT_PAGE_TYPES[attributes.page_type],
      point: {
        __typename: "Point",
        x: attributes.point[0],
        y: attributes.point[1],
      },
    },
    size: {
      __typename: "Point",
      height: attributes.data.height,
      width: attributes.data.width,
    },
    meetingId: meeting.data.gid,
    ...annotationGraphTypeFromSocket(socketData),
  };
}

export const addedEvent = [
  "annotation.added",
  (meeting, { annotation: { data } }) => {
    const documentId = data.relationships.document.data.gid;
    const newAnnotation = createNewAnnotationFromSocketData(data);
    const newAnnotationId = newAnnotation.id;
    // We need to make sure we do not add an annotation twice to the graph cache.
    // A mutation might have already taken care of this.
    const hasAnnotation = meeting.documentBundle.documents.edges.reduce((accum, docEdge) => {
      return (
        accum || docEdge.node.annotations.edges.find((edge) => edge.node.id === newAnnotationId)
      );
    }, false);
    if (hasAnnotation) {
      return null;
    }
    const meetingWithNewAnnotation = updateDocumentNode(
      meeting,
      (docNode) => docNode.id === documentId,
      (docNode) => ({
        ...docNode,
        annotations: {
          ...docNode.annotations,
          edges: [
            ...docNode.annotations.edges,
            {
              node: newAnnotation,
              __typename: "AnnotationEdge",
            },
          ],
        },
      }),
    );
    const { annotationDesignationId } = newAnnotation;
    return annotationDesignationId
      ? updateDesignationOnDocument(
          meetingWithNewAnnotation,
          annotationDesignationId,
          (designationNode) => ({
            ...designationNode,
            fulfilled: true,
          }),
        )
      : meetingWithNewAnnotation;
  },
];

export const textUpdatedEvent = [
  "annotation.text.updated",
  (meeting, { annotation: annotationId, text, width }) => {
    return updateAnnotationOnDocument(meeting, annotationId, (annotationNode) => ({
      ...annotationNode,
      text,
      size: {
        ...annotationNode.size,
        width,
      },
    }));
  },
];

export const removedEvent = [
  "annotation.removed",
  (meeting, { annotation: annotationId }) => {
    return updateDocumentNode(
      meeting,
      (docNode) => docNode.annotations.edges.find((annoEdge) => annoEdge.node.id === annotationId),
      (docNode) => {
        const { annotations, annotationDesignations } = docNode;
        const annotationEdge = annotations.edges.find(
          (annoEdge) => annoEdge.node.id === annotationId,
        );
        const { annotationDesignationId } = annotationEdge ? annotationEdge.node : {};
        const newAnnotationDesignations = annotationDesignationId
          ? {
              ...annotationDesignations,
              edges: annotationDesignations.edges.map((edge) => {
                return edge.node.id === annotationDesignationId
                  ? { ...edge, node: { ...edge.node, fulfilled: false } }
                  : edge;
              }),
            }
          : annotationDesignations;
        return {
          ...docNode,
          annotationDesignations: newAnnotationDesignations,
          annotations: {
            ...docNode.annotations,
            edges: docNode.annotations.edges.filter(
              (annoEdge) => annoEdge.node.id !== annotationId,
            ),
          },
        };
      },
    );
  },
];

export const movedEvent = [
  "annotation.moved",
  (meeting, { annotation: annotationId, point: [x, y] }) => {
    return updateAnnotationOnDocument(meeting, annotationId, (annotationNode) => ({
      ...annotationNode,
      location: {
        ...annotationNode.location,
        point: {
          ...annotationNode.location.point,
          x,
          y,
        },
      },
    }));
  },
];

export const resizedEvent = [
  "annotation.resized",
  (meeting, { annotation: annotationId, height, width }) => {
    return updateAnnotationOnDocument(meeting, annotationId, (annotationNode) => ({
      ...annotationNode,
      size: {
        ...annotationNode.size,
        height,
        width,
      },
    }));
  },
];

export const changeAssignedSignerEvent = [
  "change_assigned_signer",
  (meeting, { hint, signer_role: signerRole, annotation_designation_id: designationId }) => {
    return updateDesignationOnDocument(meeting, designationId, (designationNode) => ({
      ...designationNode,
      hint,
      signerRole: {
        ...designationNode.signerRole,
        index: signerRole.index,
        role: signerRole.type.toUpperCase(),
      },
    }));
  },
];
